import {
  ConfigurableProperty,
  DigitalTwin,
  DigitalTwinPropertyObject,
  DigitalTwinSettingCreationRequest,
} from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { BASE_SETTING_PRIO, DEVIATION_SETTING_PRIORITIES } from 'api/digitalTwin/digitalTwinConstants'
import React, { ReactNode, RefObject, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Datetime from 'utils/datetime/datetime'
import {
  checkSettingInstructionOverlapsWithEventSettings,
  EventChangeRequest,
  EventPost,
  SettingOrTimeSeriesInstructions,
} from 'views/EventsView/components/events.helper'
import { useAuth } from '../AuthContext/AuthContext'
import { getOpenedSettingPriority } from '../DigitalTwinSettingEditor/editorSetting.helper'
import InfoBanner from '../InfoBanner/InfoBanner'
import { UnitSettingFormData } from '../UnitSettingForm/UnitSettingForm'
import {
  applyCopiedData,
  CopySettingControls,
  discardCopiedData,
  getCopySettingControls,
  hasSettingChanged,
} from './DigitalTwinSettingDialog.helper'
import DigitalTwinSettingDialogActions from './DigitalTwinSettingDialogActions'
import DigitalTwinSettingDialogContent, {
  CreateSettingData,
  getDefaultValuesWhenTimeChanged,
} from './DigitalTwinSettingDialogContent'

type DigitalTwinSettingDialogProps = {
  initData: null | CreateSettingData
  defaultValues?: Partial<UnitSettingFormData>
  propertyItems: DigitalTwinPropertyObject[] | SettingOrTimeSeriesInstructions[]
  configurableProperties: ConfigurableProperty[]
  digitalTwin: DigitalTwin
  allowEmpty: boolean
  priorities: SystemSettingPriorityLevel[]
  baseSettingPriorities?: SystemSettingPriorityLevel[]
  isBaseSettings: boolean
  hideActions?: boolean
  operationalEvent?: EventPost | EventChangeRequest
  hasUnitAndProperty: boolean
  isCollectedSettings?: boolean
  disable?: boolean
  submitLabel?: string
  onClose: () => void
  onRequestClose: () => void
  onSettingModified?: () => void
  setSettingInstructionsData?: (data: any) => void
  /** Told the copy the user is making when they copy the setting, and null when they leave it for another setting. */
  onCopyChange?: (copy: CreateSettingData | null) => void
  /** Told whether the form holds changes that closing it would lose. */
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void
  /** Replaces `onClose` after a successful save; `savedSetting` is null when the save stored several settings. */
  onSaveSuccess?: (submittedSetting: DigitalTwinSettingCreationRequest, savedSetting: DigitalTwinSetting | null) => void
  scrollContainerRef?: RefObject<HTMLDivElement | null>
  renderLayout?: (layout: {
    content: ReactNode
    actions: ReactNode
    newSetting: DigitalTwinSettingCreationRequest
  }) => ReactNode
}

export function DigitalTwinSettingDialog({
  hideActions,
  initData,
  configurableProperties,
  digitalTwin,
  priorities,
  baseSettingPriorities,
  isBaseSettings,
  operationalEvent,
  propertyItems,
  hasUnitAndProperty,
  isCollectedSettings = false,
  disable = false,
  submitLabel,
  onClose,
  onRequestClose,
  onSettingModified,
  setSettingInstructionsData,
  onCopyChange,
  onUnsavedChangesChange,
  onSaveSuccess,
  scrollContainerRef,
  renderLayout,
}: DigitalTwinSettingDialogProps): ReactNode {
  const { t } = useTranslation()
  const { hasAccess } = useAuth()
  const hasAccessToTimeseries =
    hasAccess({ submodule: 'optimizesettings_time_series', module: 'optimizesettings' }) ||
    hasAccess({ submodule: 'optimizesettings_time_series', module: 'events' })
  const hasExcludeSettingAccess = hasAccess({ submodule: 'optimizesettings_exclude' })

  const [newSetting, setNewSetting] = useState<DigitalTwinSettingCreationRequest | null>(null)
  /** The setting as the form opened it, kept apart from the form's edits; a change is any difference from it. */
  const [openedSetting, setOpenedSetting] = useState<DigitalTwinSettingCreationRequest | null>(null)
  const [valueHelperText, setValueHelperText] = useState('')

  const [copiedInitData, setCopiedInitData] = useState<CreateSettingData | null>(null)
  // Counter to force remount of form components when copying (avoids controlled/uncontrolled MUI warnings)
  const [copyKey, setCopyKey] = useState(0)

  const effectiveInitData = copiedInitData ?? initData

  const propertySelected = !!newSetting?.attribute

  // Filter property items to only include writable objects
  const writablePropertyItems: any[] = useMemo(() => {
    const isWritableObject = (p: any): boolean => {
      // If any property for this unit has write set to false, user should not be able to create setting on it
      return p?.configurable_properties?.some((c: any) => c.write !== false) ?? false
    }

    return propertyItems.filter(isWritableObject)
  }, [propertyItems])

  const activeConfigurableProperty: ConfigurableProperty | undefined = useMemo(() => {
    return configurableProperties.find((p) => p.name === newSetting?.name && p.attribute === newSetting?.attribute)
  }, [configurableProperties, newSetting])

  // Reset copiedInitData when initData changes (e.g., opening a different setting)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset when initData reference changes
  useEffect(() => {
    setCopiedInitData(null)
    onCopyChange?.(null)
  }, [initData])

  useEffect(() => {
    if (effectiveInitData) {
      const priority = getOpenedSettingPriority({
        setting: effectiveInitData,
        priorities,
        configurableProperties,
        isCollectedSettings,
      })
      const opened = initializeNewSetting(effectiveInitData, priority, isBaseSettings)
      setOpenedSetting(structuredClone(opened))
      setNewSetting(opened)
    } else {
      setOpenedSetting(null)
      setNewSetting(null)
    }

    setInvalidValuesIndex([])
    setValueHelperText('')
  }, [effectiveInitData, priorities, configurableProperties, isCollectedSettings, isBaseSettings])

  // save the index of the invalid values when creating time series setting
  const [invalidValuesIndex, setInvalidValuesIndex] = useState<number[]>([])

  const hasUnsavedChanges = !!newSetting && !!openedSetting && hasSettingChanged(newSetting, openedSetting)

  const hasSavableChanges = hasUnsavedChanges && activeConfigurableProperty?.write !== false

  useEffect(() => {
    onUnsavedChangesChange(hasSavableChanges)
    return () => onUnsavedChangesChange(false)
  }, [hasSavableChanges, onUnsavedChangesChange])

  const updateNewSetting = (data: DigitalTwinSettingCreationRequest): void => {
    // If time range has changed, add new values for the parts of the range that were not previously included.
    if (
      data.start_time &&
      data.end_time &&
      (data.start_time != newSetting?.start_time || data.end_time != newSetting?.end_time) &&
      (data.start_time != newSetting?.start_time || data.end_time != newSetting?.end_time) &&
      data.value &&
      typeof data.value === 'object'
    ) {
      data.value = getDefaultValuesWhenTimeChanged(data, data.start_time, data.end_time)

      setInvalidValuesIndex([])
      setValueHelperText('')
    }

    // If priority has changed to something other than DEVIATION priority levels,
    // clear excluded_priority_layers since only DEVIATION priorities can have excluded layers
    const isDeviationPriority = DEVIATION_SETTING_PRIORITIES.some((p) => p.priority_level === data.priority)

    if (!isDeviationPriority && newSetting?.priority !== data.priority) {
      data.excluded_priority_layers = []
    }

    data.isBaseSetting = isBaseSettings
    setNewSetting(data)
  }

  const { onCopy, copyActions }: CopySettingControls = getCopySettingControls({
    newSetting,
    isBaseSettings,
    hasAccessToTimeseries,
    hasSettingInstructionsData: !!setSettingInstructionsData,
    onApplyCopiedData: (copiedData) => {
      applyCopiedData({
        copiedData,
        setCopyKey,
        setCopiedInitData,
        onCopyChange,
      })
    },
  })

  const handleBackToOriginal = (): void => {
    discardCopiedData({ setCopyKey, setCopiedInitData, onCopyChange })
  }

  if (!newSetting) {
    return <></>
  }

  const newSettingOverlapsEventSetting = checkSettingInstructionOverlapsWithEventSettings(newSetting, operationalEvent)

  // Bypass disabled onSubmit for when Exclude setting (Advanced settings) is updated. This allows the setting to be excluded in different optimization, for example Follow-up.
  const initialExcludedLayers = (effectiveInitData?.excluded_priority_layers ?? []).slice().sort((a, b) => a - b)
  const updatedExcludedLayers = (newSetting.excluded_priority_layers ?? []).slice().sort((a, b) => a - b)
  const allowBypassDisabledOnSubmitForExcludeLayerSetting =
    isCollectedSettings &&
    disable &&
    hasExcludeSettingAccess &&
    effectiveInitData?.id !== undefined &&
    initialExcludedLayers.join(',') !== updatedExcludedLayers.join(',')

  const content = (
    <>
      {newSettingOverlapsEventSetting && (
        <InfoBanner
          text={t(
            'This setting overlaps with an existing setting in this event. Please modify the existing setting or delete it and create a new one'
          )}
          style="warning"
        />
      )}
      <DigitalTwinSettingDialogContent
        key={`setting-content-${copyKey}`}
        propertyItems={writablePropertyItems}
        digitalTwin={digitalTwin}
        allowEmpty={false}
        priorities={priorities}
        baseSettingPriorities={baseSettingPriorities}
        hasUnitAndProperty={hasUnitAndProperty}
        isBaseSettings={isBaseSettings}
        isCollectedSettings={isCollectedSettings}
        disableAll={disable || (newSetting.isTimeSeries && !hasAccessToTimeseries)}
        propertySelected={propertySelected}
        invalidValuesIndex={invalidValuesIndex}
        valueHelperText={valueHelperText}
        initData={effectiveInitData}
        newSetting={newSetting}
        updateNewSetting={updateNewSetting}
        configurableProperties={configurableProperties}
        activeConfigurableProperty={activeConfigurableProperty}
        operationalEvent={operationalEvent}
        scrollContainerRef={scrollContainerRef}
      />
    </>
  )
  const actions = !hideActions && (
    <DigitalTwinSettingDialogActions
      initData={effectiveInitData}
      newSetting={newSetting}
      activeConfigurableProperty={activeConfigurableProperty}
      digitalTwin={digitalTwin}
      disable={disable}
      disableOnSave={newSettingOverlapsEventSetting}
      hasUnsavedChanges={hasUnsavedChanges}
      hasSavableChanges={hasSavableChanges}
      disableOnCopy={hasUnsavedChanges || effectiveInitData?.priority === BASE_SETTING_PRIO.priority_level}
      bypassDisabledOnSubmitForExcludeLayerSetting={allowBypassDisabledOnSubmitForExcludeLayerSetting}
      submitLabel={submitLabel}
      onClose={onClose}
      onRequestClose={onRequestClose}
      onSettingModified={onSettingModified}
      onSaveSuccess={onSaveSuccess}
      onInvalidIndexChange={(invalidValuesIndexes, valueHelperText) => {
        setInvalidValuesIndex(invalidValuesIndexes)
        setValueHelperText(valueHelperText)
      }}
      setSettingInstructionsData={setSettingInstructionsData}
      onCopy={onCopy}
      copyActions={copyActions}
      onBackToOriginal={copiedInitData ? handleBackToOriginal : undefined}
    />
  )

  if (renderLayout) {
    return renderLayout({ content, actions, newSetting })
  }

  return (
    <>
      {content}
      {actions}
    </>
  )
}

function initializeNewSetting(
  setting: CreateSettingData,
  priority: number,
  isBaseSetting: boolean
): DigitalTwinSettingCreationRequest {
  // start or end time is undefined means we are creating a new setting, so we use default start and end.
  const isoNow = Datetime.getISONow()
  const startTime: ISODateTime | null =
    setting.start_time !== undefined
      ? setting.start_time
      : isBaseSetting || setting.isTimeSeries
        ? isoNow
        : Datetime.getStartOfDay(isoNow)
  const endTime: ISODateTime | null =
    setting.end_time !== undefined
      ? setting.end_time
      : isBaseSetting && !setting.isTimeSeries
        ? null
        : Datetime.getEndOfDay(isoNow)

  // If value is a list (setting is a time series setting), convert
  // [{id: 3, value: 5}, {id: 4, value: 2}, ...] => { time1: {id: 3, value: 5}, time2: {id: 4, value: 2}, ... }
  // Make sure the whole intervall from start_time to end_time is covered, inserting default values where necessary.
  // A stored value is kept as it is, 0 and null included: 0 is a real value, such as an inactive forced setting.
  // Only a setting without a value yet (a new one) gets a default: 1 when forced, otherwise 0.
  let value: number | null | { [time: ISODateTime]: { id?: number; value: number | null } }
  if (Array.isArray(setting.value) && startTime && endTime) {
    value = Datetime.getHoursBetween(startTime, endTime)
      .map((time, index) => ({ time, index }))
      .reduce(
        (
          accumulated: { [time: ISODateTime]: { id?: number; value: number | null } },
          newEntry: { time: ISODateTime; index: number }
        ) => {
          accumulated[newEntry.time] =
            setting.value && Array.isArray(setting.value)
              ? (setting.value[newEntry.index] ?? { value: null })
              : { value: null }
          return accumulated
        },
        {}
      )
  } else if (setting.value !== undefined && !Array.isArray(setting.value)) {
    value = setting.value
  } else if (setting.attribute === 'forced') {
    value = 1
  } else if (setting.attribute === 'availability') {
    value = 0
  } else {
    value = 0
  }

  return {
    level: setting.level ?? 'unit',
    name: setting.name ?? '',
    attribute: setting.attribute ?? '',
    start_time: startTime,
    end_time: endTime,
    comment: setting.comment ?? '',
    priority,
    value,
    isTimeSeries: setting.isTimeSeries,
    unit: '',
    isBaseSetting: isBaseSetting,
    operational_event: setting.operational_event,
    creator_name: setting?.creator_name,
    source: setting?.source,
    updated_at: setting?.updated_at,
    id: setting.id ?? undefined,
    excluded_priority_layers: setting.excluded_priority_layers ?? [],
  }
}
